"use client";

import { useCallback, useEffect, useState } from "react";
import { useLocale } from "@/lib/context/LocaleProvider";
import { ApiError } from "@/lib/api/client";
import { invalidateGenerations } from "@/lib/api/cache";
import {
  fetchGenerations,
  type ApiGenerationHistoryItem,
} from "@/lib/api/generations";

const HISTORY_PER_PAGE = 10;

/**
 * Paginated GET /v1/generation for the History tab / /history screen.
 * Fetches only while `enabled` is true (e.g. History tab is active).
 */
export function useGenerationHistory(enabled: boolean) {
  const { locale, hydrated: localeHydrated, t } = useLocale();

  const [items, setItems] = useState<ApiGenerationHistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled || !localeHydrated) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, meta } = await fetchGenerations({
          page,
          perPage: HISTORY_PER_PAGE,
          locale,
        });
        if (cancelled) return;
        setItems(data);
        setTotalPages(Math.max(1, meta.pagination?.totalPages ?? 1));
        setLoaded(true);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load generation history", err);
        setItems([]);
        setTotalPages(1);
        setError(
          err instanceof ApiError ? err.message : t.authErrorFallback,
        );
        setLoaded(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    page,
    locale,
    localeHydrated,
    reloadKey,
    t.authErrorFallback,
  ]);

  const setHistoryPage = useCallback(
    (nextPage: number) => {
      setPage((prev) => {
        const clamped = Math.max(1, Math.min(nextPage, totalPages));
        return clamped === prev ? prev : clamped;
      });
    },
    [totalPages],
  );

  const retry = useCallback(() => {
    // Bypass the cached page — a retry after an error must re-hit the network.
    invalidateGenerations();
    setReloadKey((k) => k + 1);
  }, []);

  return {
    items,
    page,
    totalPages,
    loading,
    error,
    loaded,
    setHistoryPage,
    retry,
  };
}
